import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { queryClient } from "App";
import PaymentModeComponent from "app/components/common/PaymentModeComponent";
import useFolderWiseFileUploader from "app/hooks/upload/useFolderWiseFileUploader";
import { selectActiveSession } from "app/modules/schools/selectors";
import { addCompletedExpenseDetail } from "app/services/ledgerAccount";
import { ValidateFileSize } from "app/utils/helper";
import {
  CustomTextArea,
  CustomTextField,
} from "assets/styles/globalStyledComponent";
import CommonDialog from "app/components/common/Dialogs/CommonDialog";

// ============ Main Component ============
const AddMoreExpenseDialog = ({ open, onClose, prefilledData }) => {
  const selectedSession = useSelector(selectActiveSession);
  const { handleUploadImage } = useFolderWiseFileUploader();
  const paymentDetailsRef = useRef(null);

  // ============ State Management ============
  const [state, setState] = useState({
    image: null,
    purpose: prefilledData?.purpose || "",
    date: new Date(),
    amount: "",
  });

  const [errors, setErrors] = useState({});

  // ============ Handlers ============
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const { valid, message } = ValidateFileSize(file, {
      validateImage: true,
      validatePDF: true,
      imageLimit: 0.5 * 1048576, // 0.5 MB
    });

    if (!valid) {
      toast.error(message);
      return;
    }

    setState((prev) => ({
      ...prev,
      image: {
        file: file,
        link: URL.createObjectURL(file),
      },
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!state.purpose) {
      newErrors.purpose = "Purpose is required";
    }
    if (!state.amount || state.amount <= 0) {
      newErrors.amount = "Amount is required and must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (paymentData) => {
    if (!validateForm()) {
      return;
    }

    try {
      let attachmentUrl = state.image?.file
        ? await handleUploadImage(
            true,
            state.image.file,
            "withSession",
            "LEDGER",
            "EXPENSES"
          )
        : { fileURL: null };

      const payload = {
        session: selectedSession,
        expenseSubHeadDocId: prefilledData?.expenseSubHeadDocId,
        ...(attachmentUrl && {
          attachmentUrl: attachmentUrl?.fileURL,
        }),
        ...(prefilledData?.vehicleNumber && {
          vehicleNumber: prefilledData?.vehicleNumber || "",
        }),
        purpose: state.purpose,
        recipientPersonRole:
          prefilledData?.recipientPerson?.personRole || "Staff",
        recipientPersonDocId: prefilledData?.recipientPerson?.personDocId,
        expenseDate: state.date,
        paymentDetails: paymentData,
        ...(prefilledData?.viaPerson?._id && {
          viaPersonDocId: prefilledData?.viaPerson?._id,
        }),
      };

      addCompletedExpenseDetailMutate(payload);
      setState({
        image: null,
        purpose: "",
        date: new Date(),
        amount: "",
      });
      onClose();
      queryClient.invalidateQueries(["table-data"]);
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  // ============ API Mutations ============
  const { mutate: addCompletedExpenseDetailMutate } = useMutation(
    addCompletedExpenseDetail,
    {
      onSuccess: (res) => {
        toast.success(res?.data?.message);
        queryClient.invalidateQueries(["table-data"]);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message);
      },
    }
  );

  // ============ Render ============
  return (
    <CommonDialog open={open} onClose={onClose} title="Add More Expense">
      <DialogContent>
        <Box>
          <Grid container rowSpacing={1.5} columnSpacing={1} mt={1}>
            {/* Purpose */}
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontSize: "1rem",
                  lineHeight: "1.2rem",
                  marginBottom: "10px",
                  fontWeight: 500,
                  color: "#0C2F49",
                }}
              >
                Purpose
              </Typography>
              <CustomTextArea
                placeholder="Enter Purpose"
                name="purpose"
                value={state.purpose}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, purpose: e.target.value }))
                }
              />
              {errors.purpose && (
                <Typography
                  sx={{ color: "red", fontSize: "0.875rem", mt: 0.5 }}
                >
                  {errors.purpose}
                </Typography>
              )}
            </Grid>

            {/* Expense Date */}
            <Grid item xs={6} md={4}>
              <Typography
                sx={{
                  fontSize: "1rem",
                  lineHeight: "1.2rem",
                  marginBottom: "10px",
                  fontWeight: 500,
                  color: "#0C2F49",
                }}
              >
                Expense Date
              </Typography>
              <DatePicker
                inputFormat="DD-MM-YYYY"
                name="expenseDate"
                value={state.date}
                onChange={(newDate) => {
                  setState((prev) => ({
                    ...prev,
                    date: newDate,
                  }));
                }}
                maxDate={new Date()}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    placeholder="Select"
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>

            {/* Expended Amount */}
            <Grid item xs={6} md={4}>
              <Typography
                sx={{
                  fontSize: "1rem",
                  lineHeight: "1.2rem",
                  marginBottom: "10px",
                  fontWeight: 500,
                  color: "#0C2F49",
                }}
              >
                Expended Amount
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                placeholder="Enter Amount"
                type="number"
                value={state.amount}
                name="amount"
                onChange={(e) =>
                  setState((prev) => ({ ...prev, amount: e.target.value }))
                }
              />
              {errors.amount && (
                <Typography
                  sx={{ color: "red", fontSize: "0.875rem", mt: 0.5 }}
                >
                  {errors.amount}
                </Typography>
              )}
            </Grid>

            {/* Attachment Upload */}
            <Grid item xs={6} md={4}>
              <Typography
                sx={{
                  fontSize: "1rem",
                  lineHeight: "1.2rem",
                  marginBottom: "10px",
                  fontWeight: 500,
                  color: "#0C2F49",
                }}
              >
                Attachment(if any)
              </Typography>
              <Box>
                <img
                  src={
                    state?.image?.link
                      ? state?.image?.link
                      : "/image-placeholder.jpeg"
                  }
                  style={{
                    height: "100px",
                    width: "100px",
                    objectFit: "cover",
                    marginTop: "10px",
                    marginBottom: "10px",
                  }}
                  alt="expense attachment"
                />
              </Box>
              <Box>
                <Button variant="contained" color="secondary" component="label">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    hidden
                    onChange={handleFileUpload}
                  />
                  Upload
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Stack mt={2}>
          <PaymentModeComponent
            amount={state.amount}
            formId="add-more-exp-pay"
            onClickSubmit={handleSubmit}
          />
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-more-exp-pay"
          color="secondary"
          variant="contained"
        >
          Submit
        </Button>
      </DialogActions>
    </CommonDialog>
  );
};

export default AddMoreExpenseDialog;
